import { Store01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useAction, useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { AddPaidBranchModal } from "@/components/modals/add-paid-branch-modal";
import { CreateBranchModal } from "@/components/modals/create-branch-modal";
import { EditBranchModal } from "@/components/modals/edit-branch-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { StepUpTotpDialog } from "@/components/ui/step-up-totp-dialog";
import {
	type SourceType,
	SUBSCRIPTION_STATUS_VARIANTS,
} from "@/features/billing/lib/constants";
import { BranchesTable } from "@/features/branches/_components/branches-table";
import { chooseBranchCreationMode } from "@/features/branches/lib/choose-creation-mode";
import { useMe } from "@/hooks/use-me";
import { useRequireStepUp } from "@/hooks/use-require-step-up";
import { handleConvexError } from "@/lib/errors";
import type { CreateBranchInput } from "@/lib/schemas/branch";
import { toast } from "@/lib/toast";
import type { Branch } from "@/lib/types/branch";
import type { SubscriptionStatus } from "@/lib/types/subscriptions";

type BranchesSearch = {
	activated?: string;
	charge_status?: string;
};

export const Route = createFileRoute("/(dashboard)/settings/branches")({
	component: BranchesPage,
	validateSearch: (search: Record<string, unknown>): BranchesSearch => ({
		activated:
			typeof search.activated === "string" ? search.activated : undefined,
		charge_status:
			typeof search.charge_status === "string"
				? search.charge_status
				: undefined,
	}),
});

function BranchesPage() {
	const navigate = useNavigate();
	const { activated, charge_status } = useSearch({
		from: "/(dashboard)/settings/branches",
	});

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesQuery = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId, includeInactive: true } : "skip",
	);
	const subscriptionsQuery = useQuery(
		api.features.subscriptions.web.listByOrg,
		orgId ? { orgId } : "skip",
	);
	const ownedCount = useQuery(
		api.features.branches.web.countOwnedByUser,
		orgId ? { orgId } : "skip",
	);

	const branches: Branch[] = branchesQuery ?? [];
	const isLoading = branchesQuery === undefined;

	const subscriptionStatusMap = useMemo(() => {
		const map = new Map<string, SubscriptionStatus>();
		if (subscriptionsQuery) {
			for (const sub of subscriptionsQuery) {
				if (Object.hasOwn(SUBSCRIPTION_STATUS_VARIANTS, sub.status)) {
					map.set(sub.branchId, sub.status as SubscriptionStatus);
				}
			}
		}
		return map;
	}, [subscriptionsQuery]);

	const [pendingBranchId, setPendingBranchId] = useState<Id<"branches"> | null>(
		null,
	);

	const pendingBranchExists =
		pendingBranchId !== null && branches.some((b) => b._id === pendingBranchId);

	const pendingSubscription = useQuery(
		api.features.subscriptions.web.getSubscription,
		orgId && pendingBranchId && pendingBranchExists
			? {
					orgId: orgId as Id<"organizations">,
					branchId: pendingBranchId,
				}
			: "skip",
	);

	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [addPaidModalOpen, setAddPaidModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);

	const createBranch = useMutation(api.features.branches.web.create);
	const updateBranch = useMutation(api.features.branches.web.update);
	const deleteBranch = useMutation(api.features.branches.web.remove);
	const payFirstInvoice = useAction(
		api.features.subscriptions.web.payFirstInvoice,
	);
	const stepUp = useRequireStepUp();

	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isPaidSubmitting, setIsPaidSubmitting] = useState(false);

	useEffect(() => {
		const stored = sessionStorage.getItem("pendingBranch");
		if (!stored) return;
		try {
			const { branchId, ts } = JSON.parse(stored) as {
				branchId: string;
				ts: number;
			};
			const THIRTY_MINUTES = 30 * 60 * 1000;
			if (Date.now() - ts > THIRTY_MINUTES) {
				sessionStorage.removeItem("pendingBranch");
				return;
			}
			setPendingBranchId(branchId as Id<"branches">);
		} catch {
			sessionStorage.removeItem("pendingBranch");
		}
	}, []);

	useEffect(() => {
		if (!pendingBranchId || branchesQuery === undefined) return;
		if (!branches.some((b) => b._id === pendingBranchId)) {
			sessionStorage.removeItem("pendingBranch");
			setPendingBranchId(null);
		}
	}, [pendingBranchId, branchesQuery, branches]);

	useEffect(() => {
		if (!activated || branchesQuery === undefined) return;

		const statusFromMap = subscriptionStatusMap.get(activated);
		const statusFromQuery =
			pendingBranchId === (activated as Id<"branches">)
				? pendingSubscription?.status
				: undefined;
		const isActive = statusFromMap === "active" || statusFromQuery === "active";

		if (!isActive) return;

		toast.success(<>Branch activated!</>);
		sessionStorage.removeItem("pendingBranch");
		setPendingBranchId(null);
		navigate({ to: "/settings/branches", search: {}, replace: true });
	}, [
		activated,
		branchesQuery,
		subscriptionStatusMap,
		pendingBranchId,
		pendingSubscription?.status,
		navigate,
	]);

	useEffect(() => {
		if (!charge_status) return;

		if (charge_status === "successful") {
			toast.success(<>Payment confirmed. Activating your branch...</>);
		} else if (charge_status === "failed" || charge_status === "expired") {
			toast.error(<>Payment was not completed. Please try again.</>);
		}

		navigate({
			to: "/settings/branches",
			search: (prev) => {
				const next = { ...prev };
				delete next.charge_status;
				return next;
			},
			replace: true,
		});
	}, [charge_status, navigate]);

	const handleEdit = useCallback((branch: Branch) => {
		setSelectedBranch(branch);
		setEditModalOpen(true);
	}, []);

	const handleDelete = useCallback((branch: Branch) => {
		setBranchToDelete(branch);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!branchToDelete || !orgId) return;
		const branch = branchToDelete;
		setDeleteDialogOpen(false);
		await stepUp.runWithStepUp({
			intent: "branch:delete",
			title: "Delete branch?",
			description: `Enter your authenticator code to delete "${branch.name}".`,
			action: async (stepUpToken) => {
				setIsDeleting(true);
				try {
					await deleteBranch({ orgId, branchId: branch._id, stepUpToken });
					toast.success(<>Branch deleted</>);
					setBranchToDelete(null);
				} catch (error) {
					handleConvexError(error, "Failed to delete branch");
				} finally {
					setIsDeleting(false);
				}
			},
		});
	}, [branchToDelete, orgId, deleteBranch, stepUp]);

	const handleCreateBranch = useCallback(
		async (data: CreateBranchInput) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createBranch({
					orgId: orgId as Id<"organizations">,
					name: data.name,
					address: data.address,
					taxId: data.taxId,
					staffCommissionRate: data.staffCommissionRate,
				});
				toast.success(<>Branch created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create branch");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createBranch],
	);

	const handleUpdateBranch = useCallback(
		async (data: CreateBranchInput) => {
			if (!orgId || !selectedBranch) return;

			setIsUpdating(true);
			try {
				await updateBranch({
					orgId,
					branchId: selectedBranch._id,
					name: data.name,
					address: data.address,
					taxId: data.taxId,
					staffCommissionRate: data.staffCommissionRate,
				});
				toast.success(<>Branch updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update branch");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, selectedBranch, updateBranch],
	);

	const handleAddBranchClick = useCallback(() => {
		const mode = chooseBranchCreationMode(ownedCount);
		if (mode === "loading") return;
		if (mode === "free") setCreateModalOpen(true);
		else setAddPaidModalOpen(true);
	}, [ownedCount]);

	const handleAddPaidBranch = useCallback(
		async ({
			branchData,
			billingCycle,
			sourceType,
			phoneNumber,
		}: {
			branchData: CreateBranchInput;
			billingCycle: "monthly" | "yearly";
			sourceType: SourceType;
			phoneNumber?: string;
		}) => {
			if (!orgId) throw new Error("No organization");

			setIsPaidSubmitting(true);
			try {
				const { branch, subscription } = await createBranch({
					orgId: orgId as Id<"organizations">,
					name: branchData.name,
					address: branchData.address,
					taxId: branchData.taxId,
					staffCommissionRate: branchData.staffCommissionRate,
					billingCycle,
				});

				// Track the branch immediately so it can be recovered from the
				// Billing page if the subsequent subscription/payment steps fail.
				setPendingBranchId(branch._id as Id<"branches">);

				const result = await payFirstInvoice({
					orgId: orgId as Id<"organizations">,
					branchId: branch._id,
					subscriptionId: subscription._id,
					sourceType,
					phoneNumber,
					returnUri: globalThis.location.href,
				});

				return {
					branchId: branch._id as string,
					qrCodeUri: result.qrCodeUri ?? null,
					authorizeUri: result.authorizeUri ?? null,
				};
			} catch (error) {
				handleConvexError(error, "Failed to create branch");
				throw error;
			} finally {
				setIsPaidSubmitting(false);
			}
		},
		[orgId, createBranch, payFirstInvoice],
	);

	const handleBranchCreated = useCallback((branchId: string) => {
		setPendingBranchId(branchId as Id<"branches">);
	}, []);

	const handleManageBilling = useCallback(
		(branch: Branch) => {
			navigate({ to: "/settings/billing", search: { branch: branch._id } });
		},
		[navigate],
	);

	return (
		<PageContent
			title="Branches"
			controls={
				<Can scope="branches.create">
					<ActionHeaderButton
						label="Add Branch"
						onClick={handleAddBranchClick}
						disabled={ownedCount === undefined}
					/>
				</Can>
			}
		>
			<BranchesTable
				data={branches}
				loading={isLoading}
				onEdit={handleEdit}
				onDelete={handleDelete}
				onManageBilling={handleManageBilling}
				subscriptionStatusMap={subscriptionStatusMap}
				emptyState={
					<EmptyStateWithSkeleton
						title="Add your first branch"
						description="Each pharmacy location is a branch. Configure tax info and business hours, then link a POS device to start running sales."
						actionLabel="Add branch"
						onAction={handleAddBranchClick}
						icon={
							<HugeiconsIcon
								icon={Store01Icon}
								className="h-5 w-5 text-neutral-700"
								strokeWidth={2.2}
							/>
						}
					/>
				}
			/>

			<CreateBranchModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSubmit={handleCreateBranch}
				isSubmitting={isCreating}
			/>

			<AddPaidBranchModal
				open={addPaidModalOpen}
				onOpenChange={setAddPaidModalOpen}
				onSubmit={handleAddPaidBranch}
				onBranchCreated={handleBranchCreated}
				subscriptionStatus={
					pendingBranchId
						? (pendingSubscription?.status ??
							subscriptionStatusMap.get(pendingBranchId))
						: undefined
				}
				isSubmitting={isPaidSubmitting}
			/>

			<EditBranchModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				branch={selectedBranch}
				onSubmit={handleUpdateBranch}
				isSubmitting={isUpdating}
			/>

			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete branch?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{branchToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<StepUpTotpDialog {...stepUp.dialogProps} />
		</PageContent>
	);
}
